import React, { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { QUESTION_TYPE_NPS_REASON } from 'modules/surveys/form/questions/constants';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { makeStyles } from "@material-ui/core";
import qs from 'query-string';
import renderHTML from 'react-render-html';
import { themedStyles } from './styles';
import { useGAEffect } from '../../../../ga';
import { thunkSaveFeedback, thunkFetchPreviewGoogleReviewUrl } from '../../redux/thunk';
import {
  QUESTION_TYPE_SELECT,
  QUESTION_TYPE_RADIO_BUTTON,
  QUESTION_TYPE_SELECT_BUTTON,
  QUESTION_TYPE_NPS_QUESTION,
  QUESTION_TYPE_SELECT_USER,
  QUESTION_TYPE_CHECK_BOX,
  QUESTION_TYPE_DATE,
  QUESTION_TYPE_FILE_UPLOAD,
} from 'modules/surveys/form/questions/constants';
import { PUBLIC_SAVE_FEEDBACK_SUCCESS, PUBLIC_SET_GOOGLE_REVIEW_URL } from 'modules/public/redux/actions';
import { htmlCss } from "../utils";
import SurveyFooter from "../SurveyPage/SurveyFooter";
import HtmlRender from '../components/HtmlRender';

const useStyles = makeStyles(themedStyles);

const cssCommon = `
  .textHeader {
    background-color: #EC681A;
    padding: 5px 0;
    font-size: 13px;
  }
`;

const cssSettingEndPage = `
  .textHeader {
    background-color: #EC681A;
    padding: 5px 0;
    font-size: 13px;
  }
`;

const NPS_REASON_TEXTAREA = '{%NPS_REASON_TEXTAREA%}';
const GMB_LINK = '{%GMB_LINK%}';
const GMB_URL = '{%GMB_URL%}';
const COPY_BUTTON_WITH_LABEL = /^\{%COPY_BUTTON(?::(.+))?%\}$/;
const HIGH_RATING_PREVIEW_DUMMY_TEXT = 'ここにNPS理由が表示されます';
const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:']);
const INVALID_GOOGLE_REVIEW_URL_CHARS = /[\u0000-\u001F\u007F<>"'\s]/;
const HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

const getSafeGoogleReviewUrl = value => {
  // API 由来の値だけを対象にし、空白や属性注入に使える文字を含む値は早期に除外する。
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue || INVALID_GOOGLE_REVIEW_URL_CHARS.test(normalizedValue)) {
    return null;
  }

  try {
    // URL として解釈でき、かつ公開画面で許可する http/https の絶対URLだけを通す。
    const parsedUrl = new URL(normalizedValue);

    if (!SAFE_URL_PROTOCOLS.has(parsedUrl.protocol) || !parsedUrl.host) {
      return null;
    }

    return normalizedValue;
  } catch (error) {
    return null;
  }
};

const escapeHtml = value => value.replace(/[&<>"']/g, char => HTML_ESCAPE_MAP[char]);

const propTypes = {
  survey: PropTypes.object.isRequired,
  doc: PropTypes.object,
  saveFeedback: PropTypes.func.isRequired,
  fakeSaveFeedback: PropTypes.func.isRequired,
  fetchPreviewGoogleReviewUrl: PropTypes.func.isRequired,
  visitedPages: PropTypes.array.isRequired,
  companyUrlText: PropTypes.string.isRequired,
  shortUrl: PropTypes.string.isRequired,
  public_ga: PropTypes.object.isRequired,
  dataState: PropTypes.string.isRequired,
  google_review_url: PropTypes.string,
}

const EndPage = ({
  survey,
  doc,
  saveFeedback,
  fakeSaveFeedback,
  fetchPreviewGoogleReviewUrl,
  visitedPages,
  companyUrlText,
  shortUrl,
  public_ga,
  dataState,
  google_review_url,
}) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const isPreviewMode = pathname.includes('/s/preview/');
  const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const isHighRatingPreviewMode = isPreviewMode && query.preview_mode === 'high' && !!survey.use_end_page_html;
  const paramData = useRef({});
  const copyResetTimerRef = useRef(null);
  const [npsReasonText, setNpsReasonText] = useState('');
  const [npsScore, setNpsScore] = useState(null);
  const [hasCopiedNpsReason, setHasCopiedNpsReason] = useState(false);
  const safeGoogleReviewUrl = getSafeGoogleReviewUrl(google_review_url);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])

  useEffect(() => {
    if (!doc || !survey) {
      return;
    }

    const npsReasonQuestion = (survey.survey_pages_attributes || [])
      .flatMap(page => page.question_items || [])
      .find(q => q.question_type === QUESTION_TYPE_NPS_REASON);
    if (!npsReasonQuestion) {
      setNpsReasonText('');
      return;
    }

    const answer = (doc.answers_attributes || [])
      .find(a => +a.question_id === +npsReasonQuestion.id);
    if (answer && answer.answer_entry != null) {
      setNpsReasonText(answer.answer_entry);
    } else {
      setNpsReasonText('');
    }
    setHasCopiedNpsReason(false);
  }, [doc, survey]);

  useEffect(() => {
    if (!doc || !survey) {
      return;
    }

    const npsQuestion = (survey.survey_pages_attributes || [])
      .flatMap(page => page.question_items || [])
      .find(item => item.question_type === QUESTION_TYPE_NPS_QUESTION);

    if (!npsQuestion) {
      setNpsScore(null);
      return;
    }

    const npsAnswer = (doc.answers_attributes || [])
      .find(answer => +answer.question_id === +npsQuestion.id);

    const questionOptionId = (((npsAnswer || {}).answer_options_attributes || [])[0] || {}).question_option_id;

    if (!questionOptionId) {
      setNpsScore(null);
      return;
    }

    const questionOption = (npsQuestion.question_options_attributes || [])
      .find(option => +option.id === +questionOptionId);

    if (!questionOption) {
      setNpsScore(null);
      return;
    }

    setNpsScore(Number(questionOption.value));
  }, [doc, survey]);

  useEffect(() => () => {
    if (copyResetTimerRef.current) {
      window.clearTimeout(copyResetTimerRef.current);
    }
  }, []);

  const handleCopy = () => {
    setHasCopiedNpsReason(true);
    if (copyResetTimerRef.current) {
      window.clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = window.setTimeout(() => {
      setHasCopiedNpsReason(false);
      copyResetTimerRef.current = null;
    }, 3000);
  };

  const disablePreviewLinkNavigation = e => {
    if (!isHighRatingPreviewMode) {
      return;
    }
    const anchor = e.target.closest && e.target.closest('a');
    if (anchor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const renderHighRatingHtml = value => {
    if (!value) {
      return <></>;
    }

    const gmbAnchorPattern = new RegExp(`<a[^>]*href=["']${GMB_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*>.*?<\\/a>`, 'gi');
    const emptyHrefAnchorPattern = /<a([^>]*?)href=["']\s*["']([^>]*)>(.*?)<\/a>/gi;
    const replacedValue = (isHighRatingPreviewMode ? value.replace(gmbAnchorPattern, '') : value)
      .replace(
        new RegExp(GMB_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        () => (isHighRatingPreviewMode ? '' : (safeGoogleReviewUrl ? escapeHtml(safeGoogleReviewUrl) : ''))
      )
      .replace(isHighRatingPreviewMode ? emptyHrefAnchorPattern : /$^/, '<span$1$2>$3</span>');
    const pattern = new RegExp(`(${NPS_REASON_TEXTAREA.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|\\{%COPY_BUTTON(?::.+?)?%\\}|${GMB_LINK.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`);
    const segments = replacedValue.split(pattern);

    return (
      <div onClick={disablePreviewLinkNavigation}>
        {segments.map((segment, index) => {
          const copyButtonMatch = segment.match(COPY_BUTTON_WITH_LABEL);

          if (segment === NPS_REASON_TEXTAREA) {
            return (
              <textarea
                key={`${segment}-${index}`}
                className={classes.googleReviewTextArea}
                value={npsReasonText}
                placeholder={isHighRatingPreviewMode ? HIGH_RATING_PREVIEW_DUMMY_TEXT : ''}
                onChange={e => {
                  setNpsReasonText(e.target.value);
                  setHasCopiedNpsReason(false);
                }}
                rows={3}
              />
            );
          }

          if (copyButtonMatch) {
            const copyButtonLabel = (copyButtonMatch[1] || '').trim();
            const isIconOnlyCopyButton = !copyButtonLabel;

            return (
              <CopyToClipboard
                key={`${segment}-${index}`}
                text={npsReasonText}
                onCopy={handleCopy}
              >
                <button
                  className={`${classes.copyButton} ${hasCopiedNpsReason ? classes.copyButtonCopied : ''}`}
                >
                  {hasCopiedNpsReason && !isIconOnlyCopyButton ? 'コピーしました' : copyButtonLabel}
                  <i className={`${classes.copyButtonIcon} ${hasCopiedNpsReason ? 'el-icon-check' : 'el-icon-document-copy'}`} />
                </button>
              </CopyToClipboard>
            );
          }

          if (segment === GMB_LINK) {
            return isHighRatingPreviewMode ? (
              <span
                key={`${segment}-${index}`}
                className={classes.googleReviewLink}
              >
                ここにGMB_LINKが表示されます
              </span>
            ) : safeGoogleReviewUrl ? (
              <a
                key={`${segment}-${index}`}
                href={safeGoogleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.googleReviewLink}
              >
                {safeGoogleReviewUrl}
              </a>
            ) : null;
          }

          return <HtmlRender key={`html-${index}`} value={segment} isEndPage={true} />;
        })}
      </div>
    );
  };

  useEffect(() => {
    const url_params = window.location.search.indexOf('?') !== -1 ? window.location.search.split('?')[1] : '';
    const { area, shop, user, meta, autovalidate, ...params } = doc;

    const area_id = area ? area.id : '';
    const shop_id = shop ? shop.id : '';
    let user_id = '';
    let user_ucode = '';
    let is_blank_user = false;

    if (!!user) {
      user_id = user.id;
      user_ucode = user.name;
      is_blank_user = user_id === "";
    }

    params.answers_attributes = mergeWithBlankAnswers();
    const q = qs.parse(window.location.search, {ignoreQueryPrefix: true});

    const feedbackDoc = {
      companyUrlText,
      shortUrl,
      register_number: q.pos_id,
      url_params,
      ...params,
      area_id,
      shop_id,
      user_id,
      is_blank_user,
    };

    paramData.current = {
      shop_id: shop ? shop.uid : '',
      staff_id: user_ucode,
      pos_id: q.pos_id,
      purchase_date: q.purchase_date,
      deal_id: q.deal_id,
      customer_id: q.customer_id,
      custom01: q.p1,
      custom02: q.p2,
    };

    if (!isPreviewMode) {
      saveFeedback(feedbackDoc);
    } else {
      fakeSaveFeedback();
      const npsQuestion = (survey.survey_pages_attributes || [])
        .flatMap(page => page.question_items || [])
        .find(item => item.question_type === QUESTION_TYPE_NPS_QUESTION);
      const npsAnswer = npsQuestion ? (params.answers_attributes || []).find(answer => +answer.question_id === +npsQuestion.id) : null;
      const questionOption = (((npsAnswer || {}).answer_options_attributes || [])[0] || {});
      fetchPreviewGoogleReviewUrl({
        companyUrlText,
        shortUrl,
        shop_id,
        question_option_id: questionOption.question_option_id || null,
      });
    }
    // NOTE: 送信/プレビューAPIの二重実行を避けるため、このeffectは isPreviewMode 変化時のみ実行する。
    // saveFeedback/fakeSaveFeedback/fetchPreviewGoogleReviewUrl/survey/doc は意図的に依存配列へ含めない。
    // eslint-disable-next-line
  }, [isPreviewMode]);

  useGAEffect(public_ga, "submit", "回答送信", null)

  const mergeWithBlankAnswers = () => {
    const answers_attributes = [];
    let answeredQuestionIds = [];

    visitedPages
      .filter(item => item !== "start" && item !== "end" && item !== "finish")
      .forEach(item => {

        const questionItems = ((survey || {}).survey_pages_attributes || [])[+item].question_items.filter(item => item.question_type !== "survey_heading");

        const questionIds = questionItems.map(item => +item.id);
        answeredQuestionIds = answeredQuestionIds.concat(questionIds);
      });

    survey.survey_pages_attributes
      .forEach(surveyPage => {
        surveyPage.question_items.filter(item => item.question_type !== "survey_heading").forEach(questionItem => {
          const answer_attributes = (doc.answers_attributes || []).filter(item => answeredQuestionIds.includes(+item.question_id))
          let answer = answer_attributes.find(item => +item.question_id === +questionItem.id);

          if (!!!answer) {
            if ([
              QUESTION_TYPE_SELECT,
              QUESTION_TYPE_RADIO_BUTTON,
              QUESTION_TYPE_SELECT_BUTTON,
              QUESTION_TYPE_NPS_QUESTION
            ].includes(questionItem.question_type)) {
              answer = {
                answer_entry: '',
                question_id: questionItem.id,
                answer_options_attributes: [],
              };
            } else if (questionItem.question_type === QUESTION_TYPE_SELECT_USER) {
              answer = {
                question_id: questionItem.id,
                user_id: null,
              };

            } else if (questionItem.question_type === QUESTION_TYPE_CHECK_BOX) {
              answer = {
                answer_entry: '',
                question_id: questionItem.id,
                answer_options_attributes: []
              };

            } else if (questionItem.question_type === QUESTION_TYPE_DATE) {
              answer = {
                answer_date: null,
                answer_entry: '',
                question_id: questionItem.id,
              };

            } else if (questionItem.question_type === QUESTION_TYPE_FILE_UPLOAD) {
              answer = {
                answer_entry: '',
                question_id: questionItem.id,
                answer_image: null,
              };

            } else {
              answer = {
                answer_entry: '',
                question_id: questionItem.id,
              };
            }
          }

          answers_attributes.push(answer);
        });
      });

    return answers_attributes;
  }

  const hasHighRatingEndPage = !!survey.use_end_page_html;
  const hasNpsScore = npsScore != null;
  const hasGoogleReviewMinScore = survey.google_review_min_score != null;
  const meetsGoogleReviewScore = hasNpsScore && hasGoogleReviewMinScore && npsScore >= survey.google_review_min_score;
  const hasGoogleReviewUrl = !!safeGoogleReviewUrl;
  const isHighRating = isHighRatingPreviewMode || (hasHighRatingEndPage && meetsGoogleReviewScore && hasGoogleReviewUrl);
  const endPageMainText = isHighRating ? survey.high_rating_end_page_main_text : survey.end_page_main_text;
  const endPageHtml = isHighRating ? survey.high_rating_end_page_html : survey.end_page_html;
  const endPageCustomCss = isHighRating ? survey.high_rating_end_page_custom_css : survey.end_page_custom_css;
  const endPageMainImageUrl = isHighRating ? survey.high_rating_end_page_main_image_url : survey.end_page_main_image_url;

  const renderBody = () => {
    if (!survey.use_end_page_html) {
      return (
        <>
          <div>
            <pre className={classes.txtEndContent}>
            <HtmlRender
                value = {endPageMainText}
                isEndPage = {true}
            />
            </pre>
          </div>
          {renderHTML(htmlCss(endPageCustomCss || cssSettingEndPage))}
        </>
      )
    } else {
      return (
        <>
          {isHighRating ? renderHighRatingHtml(endPageHtml) : (
            <HtmlRender
              value = {endPageHtml}
              isEndPage = {true}
            />
          )}
          {renderHTML(htmlCss(endPageCustomCss || cssSettingEndPage))}
        </>
      );
    }
  }

  const { use_redirect } = survey;

  if (use_redirect === true && dataState === 'saved') {
    let { redirect_url } = survey;

    Object.keys(paramData.current).forEach(key => {
      redirect_url = redirect_url.replace('<%=' + key + '=%>', paramData.current[key] || '');
    });

    window.location = redirect_url;
    return null;
  }

  if (!!!use_redirect) {
    return (
      <div className={classes.root}>
        <div className={classes.contentTitle}>
          {renderHTML(survey.common_header)}
          {renderHTML(htmlCss(survey.common_css || cssCommon))}
        </div>

        {!survey.use_end_page_html && !!endPageMainImageUrl && (
          <div className={classes.imgStyle}>
            <img className={classes.img} src={endPageMainImageUrl} alt={""} />
          </div>
        )}

        <div className={classes.contentTitle}>
          <h2>{survey.page_title}</h2>
        </div>

        <div className={classes.content}>
          {!isHighRating && <h2 className="textEnd txtEndsubHeader">ありがとうございます。</h2>}
          {renderBody()}
        </div>
        <SurveyFooter classes={classes} survey={survey} defaultCss={cssCommon}/>
      </div>
    );
  } else {
    return null;
  }
}

EndPage.propTypes = propTypes;

export default connect(({ public: { survey, dataState, visitedPages, doc, google_review_url }, public_ga }) => (
  {
    survey,
    dataState,
    public_ga,
    visitedPages,
    doc,
    google_review_url,
  }),
  (dispatch) => ({
    saveFeedback: ({ companyUrlText, shortUrl, ...params }) => {
      dispatch(thunkSaveFeedback({
        companyUrlText,
        shortUrl,
        params
      }));
    },
    fakeSaveFeedback: () => {
      dispatch({ type: PUBLIC_SET_GOOGLE_REVIEW_URL, payload: { google_review_url: null } })
      dispatch({ type: PUBLIC_SAVE_FEEDBACK_SUCCESS })
    },
    fetchPreviewGoogleReviewUrl: ({ companyUrlText, shortUrl, shop_id, question_option_id }) => {
      dispatch(thunkFetchPreviewGoogleReviewUrl({
        companyUrlText,
        shortUrl,
        shop_id,
        question_option_id
      }));
    }
  })
)(EndPage);
